import React, { useEffect } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Form, Formik } from "formik";
import { Button } from "@thrivetribe/gloji.ui.atoms.button";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import {
  ModalPageHeader,
  ModalPageBody,
  ModalPageFooter,
  ModalPageWrapper,
  ModalPageHeading,
} from "../../../organisms/ModalPage";
import RadioButtonGroup from "../../../molecules/RadioButtonGroup/RadioButtonGroup";
import { update_signup_form_values } from "../../../../store/actions/signupFlowData";
import { PhysicalHealthValidationSchema } from "../../../../data/Validation/EligibilityCheckerValidation";
import FormikCheckbox from "../../../atoms/FormikCheckbox";
import CheckboxGroupContainer from "../../../atoms/CheckboxGroupContainer";
import { physicalHealthConditions } from "../../../../enums/profile/PhysicalHealthCondition";
import { PhysicalHealthProps } from "./PhysicalHealthRoot";
import { pageTitleSuffix } from "../../../../data/eligibilityChecker";
import { lowercaseFirstLetter } from "../../../../utils/StringUtils";

const PhysicalHealth: React.FC<PhysicalHealthProps> = (props: PhysicalHealthProps) => {
  const { programmeTitle, physicalHealthPageData, nextStepUrl, healthConditions } = props;

  const dispatch = useDispatch();

  const signupFormValues = useSelector(
    (state: { signupFlowData: { signupFormValues: any } }) => state.signupFlowData.signupFormValues
  );

  let history = useHistory();
  const pageTitle = `Physical health | ${programmeTitle} | ${pageTitleSuffix}`;

  let physicalHealth = null;
  if (!!healthConditions?.physicalConditions) {
    physicalHealth = healthConditions?.physicalConditions.length > 0 ? "yes" : "no";
  }

  const initialValues = {
    physicalHealth: signupFormValues?.physicalHealth ?? physicalHealth ?? "",
    physicalConditions:
      signupFormValues?.physicalConditions ??
      healthConditions?.physicalConditions.map(x => lowercaseFirstLetter(x.toString())) ??
      [],
  };

  const yesNoOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  useEffect(() => {
    window.gtag("event", "page_view", {
      page_title: pageTitle,
    });
  }, [pageTitle, programmeTitle]);

  const handleSubmit = values => {
    if (values.physicalHealth === "no") {
      // Save values to store
      dispatch(update_signup_form_values({ ...values, physicalConditions: [] }));
    } else {
      // Save values to store
      dispatch(update_signup_form_values(values));
    }
    history.push(nextStepUrl);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={PhysicalHealthValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ isSubmitting, values, touched, errors }) => (
        <Form className="eligibility-checker-form physical-health-form">
          <Helmet>
            <title>{pageTitle}</title>
          </Helmet>
          <ModalPageWrapper>
            <ModalPageBody className="with-sticky-footer">
              <ModalPageHeader
                backButtonOnClick={() => {
                  history.goBack();
                }}
              >
                Checking we're a good fit
              </ModalPageHeader>

              <ModalPageHeading>{physicalHealthPageData?.questions[0].question}</ModalPageHeading>
              <RadioButtonGroup
                options={yesNoOptions}
                name="physicalHealth"
                hasErrors={errors[`physicalHealth`] && touched["physicalHealth"]}
              />
              <Accordion square={true} elevation={0} className="eligibility-checker__accrodion">
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="large" />}
                  aria-controls={`physical-question-explanation`}
                >
                  <span>{physicalHealthPageData?.questions[0].whyQuestion}</span>
                </AccordionSummary>
                <AccordionDetails>
                  <span>{physicalHealthPageData?.questions[0].whyQuestionDetails}</span>
                </AccordionDetails>
              </Accordion>

              {values.physicalHealth === "yes" && (
                <>
                  <ModalPageHeading>Select any that apply to you</ModalPageHeading>
                  <CheckboxGroupContainer name="physicalConditions">
                    {physicalHealthConditions.map(item => (
                      <FormikCheckbox
                        key={item.value}
                        id={item.value}
                        name="physicalConditions"
                        label={item.label}
                        value={item.value}
                      />
                    ))}
                  </CheckboxGroupContainer>
                </>
              )}
            </ModalPageBody>
            <ModalPageFooter className="sticky-footer">
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                Continue
              </Button>
            </ModalPageFooter>
          </ModalPageWrapper>
        </Form>
      )}
    </Formik>
  );
};

export default PhysicalHealth;
